import 'react-toastify/dist/ReactToastify.css';
import { lazy, Suspense } from 'react';
import { ToastContainer } from 'react-toastify';
import TrackingActivationPanel from './components/shipments/TrackingActivationPanel';
import RoutesConfig from './routes';
import AuthPage from './pages/auth';
import useAuthState from 'common/src/hooks/useAuthState';
import useMfaGate from 'common/src/hooks/useMfaGate';
import useSpeciesTheme from 'common/src/hooks/useSpeciesTheme';
import SidebarLayout from './components/layouts/sidebars';
import { Spinner } from '@nextui-org/react';
import { Route, useLocation } from 'wouter';
import ReportViewPage from './pages/reports/[slug]';
import PublicClientReportPage from './pages/client-reports/public';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import DemoExpired from './pages/DemoExpired';
import OAuthConsent from './pages/auth/OAuthConsent';
import MfaChallenge from './components/auth/MfaChallenge';
import VictoriaChat from './components/victoria/VictoriaChat';

// Asistente de IA por ambiente: en Vercel, VITE_SHOW_AI_ASSISTANT=true solo
// en stage mientras se prueba el chat en AWS Lambda. Producción lo prende
// agregando la variable allí.
const SHOW_AI_ASSISTANT = import.meta.env.VITE_SHOW_AI_ASSISTANT === 'true';

// Catálogo de componentes (design.md). Solo en local: en producción
// import.meta.env.DEV es false y el módulo ni siquiera se empaqueta.
const DevComponentsPage = import.meta.env.DEV
  ? lazy(() => import('./pages/dev/components'))
  : null;

const App = () => {
  const { session, loadingProfile, demoExpired } = useAuthState();
  const {
    status: mfaStatus,
    hasFactor: hasTwoFactor,
    recheck: recheckMfa,
  } = useMfaGate(session);
  const [location] = useLocation();
  // Keep --brand-* CSS variables in sync with selectedSpecies + darkMode.
  useSpeciesTheme();

  if (DevComponentsPage && location === '/dev/components') {
    return (
      <Suspense fallback={null}>
        <DevComponentsPage />
        <TrackingActivationPanel />
      </Suspense>
    );
  }

  // Check if the current route is a public route
  const isPublicReportRoute = location.startsWith('/reports/');
  const isSharedReportRoute = location.startsWith('/r/');
  const isForgotPasswordRoute = location === '/forgot-password';
  const isResetPasswordRoute = location === '/reset-password';
  const isPublicRoute =
    isPublicReportRoute ||
    isSharedReportRoute ||
    isForgotPasswordRoute ||
    isResetPasswordRoute;

  if (loadingProfile && !isPublicRoute) {
    return (
      <div className='h-screen w-screen flex items-center justify-center'>
        <Spinner size='lg' />
      </div>
    );
  }

  // Reportes para clientes compartidos por link: se abren sin login.
  if (isSharedReportRoute) {
    return <Route path='/r/:token' component={PublicClientReportPage} />;
  }

  // Allow public access to report view pages
  if (isPublicReportRoute) {
    return (
      <>
        <ToastContainer />
        <Route path='/reports/:slug' component={ReportViewPage} />
      </>
    );
  }

  // Allow public access to forgot password page
  if (isForgotPasswordRoute) {
    return (
      <>
        <ToastContainer />
        <ForgotPassword />
      </>
    );
  }

  // Verificación en dos pasos: con un factor activo, la sesión debe validar
  // el código de la app antes de ver la plataforma, aprobar conexiones MCP o
  // cambiar la contraseña (Supabase exige aal2 para eso cuando hay factor).
  if (session && mfaStatus === 'loading') {
    return (
      <div className='h-screen w-screen flex items-center justify-center'>
        <Spinner size='lg' />
      </div>
    );
  }

  if (session && mfaStatus === 'required') {
    return (
      <>
        <ToastContainer />
        <MfaChallenge onVerified={recheckMfa} />
      </>
    );
  }

  // Allow public access to reset password page
  if (isResetPasswordRoute) {
    return (
      <>
        <ToastContainer />
        <ResetPassword />
      </>
    );
  }

  // Consentimiento OAuth (conexiones MCP): requiere sesión; si no la hay se
  // muestra el login y, al entrar, la URL (con el authorization_id) se
  // conserva y aparece el consentimiento.
  if (location === '/oauth/consent') {
    return (
      <>
        <ToastContainer />
        {session ? <OAuthConsent /> : <AuthPage />}
      </>
    );
  }

  if (session && demoExpired) {
    return (
      <>
        <ToastContainer />
        <DemoExpired />
      </>
    );
  }

  return !session ? (
    <>
      <ToastContainer />
      <AuthPage />
    </>
  ) : (
    <>
      <ToastContainer />
      <SidebarLayout
        hasTwoFactor={hasTwoFactor}
        onTwoFactorEnabled={recheckMfa}
      >
        <RoutesConfig />
      </SidebarLayout>
      <TrackingActivationPanel />
      {SHOW_AI_ASSISTANT && <VictoriaChat />}
    </>
  );
};
export default App;
